import { lazy, Suspense } from "react";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "@/lib/queryClient";
import { BrowserRouter, Route, Routes, Navigate } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { useFlowUi } from "@/hooks/useFlowUi";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/hooks/useAuth";

import PublicLayout from "@/components/layout/PublicLayout";
import AppShell from "@/components/layout/AppShell";
import { RequireAuth } from "@/components/auth/RequireAuth";
import { RoleHomeRedirect } from "@/components/auth/RoleHomeRedirect";

// Landing stays eager — it's the LCP surface for cold visits.
import Landing from "@/pages/Landing";

// Public marketing and blog — small, but rarely first paint.
const About = lazy(() => import("@/pages/About"));
const Pricing = lazy(() => import("@/pages/Pricing"));
const Contact = lazy(() => import("@/pages/Contact"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const LoadBoardsGuide = lazy(() => import("@/pages/blog/LoadBoardsGuide"));
const LoadBoardsVsBrokers = lazy(() => import("@/pages/blog/LoadBoardsVsBrokers"));

// Auth flow
const Login = lazy(() => import("@/pages/auth/Login"));
const Signup = lazy(() => import("@/pages/auth/Signup"));
const Onboarding = lazy(() => import("@/pages/auth/Onboarding"));
const OnboardingPortal = lazy(() => import("@/pages/auth/OnboardingPortal"));
const OAuthConsent = lazy(() => import("@/pages/OAuthConsent"));

// Shipper
const ShipperDashboard = lazy(() => import("@/pages/app/ShipperDashboard"));
const CreateShipment = lazy(() => import("@/pages/app/CreateShipment"));
const ShipperShipments = lazy(() => import("@/pages/app/ShipperShipments"));
const ShipmentDetail = lazy(() => import("@/pages/app/ShipmentDetail"));
const PodSubmit = lazy(() => import("@/pages/app/PodSubmit"));
const ScanBarcode = lazy(() => import("@/pages/app/driver/ScanBarcode"));
const DriverShell = lazy(() => import("@/pages/app/driver/DriverShell"));
const MobileScan = lazy(() => import("@/pages/app/driver/MobileScan"));
const MobileMismatch = lazy(() => import("@/pages/app/driver/MobileMismatch"));
const MobilePod = lazy(() => import("@/pages/app/driver/MobilePod"));
const MobileTracking = lazy(() => import("@/pages/app/driver/MobileTracking"));
const OfflineQueue = lazy(() => import("@/pages/app/driver/OfflineQueue"));
const MobileToday = lazy(() => import("@/pages/app/driver/MobileToday"));
const MobileAccount = lazy(() => import("@/pages/app/driver/MobileAccount"));

// Placeholder dashboards (grouped module — keep together)
const PlaceholderDashboards = () => import("@/pages/app/PlaceholderDashboards");
const DriverDashboard = lazy(() => PlaceholderDashboards().then(m => ({ default: m.DriverDashboard })));
const IntelligencePage = lazy(() => PlaceholderDashboards().then(m => ({ default: m.IntelligencePage })));
const GenericPage = lazy(() => PlaceholderDashboards().then(m => ({ default: m.GenericPage })));

// Admin
const CommandCentre = lazy(() => import("@/pages/app/admin/CommandCentre"));
const FreightOrchestration = lazy(() => import("@/pages/app/admin/FreightOrchestration"));
const CrmPage = lazy(() => import("@/pages/app/admin/CrmPage"));
const AvailabilityBoard = lazy(() => import("@/pages/app/admin/AvailabilityBoard"));
const IncidentsPage = lazy(() => import("@/pages/app/admin/IncidentsPage"));
const MessageTemplatesPage = lazy(() => import("@/pages/app/admin/MessageTemplatesPage"));
const KpisPage = lazy(() => import("@/pages/app/admin/KpisPage"));
const ReadinessPage = lazy(() => import("@/pages/app/admin/ReadinessPage"));
const PaymentControlsPage = lazy(() => import("@/pages/app/admin/PaymentControlsPage"));
const AutomationConsole = lazy(() => import("@/pages/app/admin/AutomationConsole"));

const SupportPage = lazy(() => import("@/pages/app/SupportPage"));
const NotificationsPage = lazy(() => import("@/pages/app/NotificationsPage"));
const SecurityCentre = lazy(() => import("@/pages/app/SecurityCentre"));
const JoinRequests = lazy(() => import("@/pages/app/JoinRequests"));
const TransporterPreview = lazy(() => import("@/pages/app/transporter/PreviewWorkspace"));
const TransporterHome = lazy(() => import("@/pages/app/transporter/TransporterHome"));
const FleetPage = lazy(() => import("@/pages/app/transporter/FleetPage"));

// Founder — heavy, gated to a single user set
const FounderShell = lazy(() => import("@/pages/app/founder/FounderShell"));
const FounderOverview = lazy(() => import("@/pages/app/founder/FounderOverview"));
const KillSwitches = lazy(() => import("@/pages/app/founder/KillSwitches"));
const LaunchReadiness = lazy(() => import("@/pages/app/founder/LaunchReadiness"));
const ProtectedRules = lazy(() => import("@/pages/app/founder/ProtectedRules"));
const ActionLog = lazy(() => import("@/pages/app/founder/ActionLog"));
const PlaceholderPanels = () => import("@/pages/app/founder/PlaceholderPanels");
const ImpersonationPanel = lazy(() => PlaceholderPanels().then(m => ({ default: m.ImpersonationPanel })));
const RevealPanel = lazy(() => PlaceholderPanels().then(m => ({ default: m.RevealPanel })));
const FeatureFlagsPage = lazy(() => import("@/pages/app/founder/FeatureFlagsPage"));
const ApprovalsQueue = lazy(() => import("@/pages/app/founder/ApprovalsQueue"));
const RecommendationVersions = lazy(() => import("@/pages/app/founder/RecommendationVersions"));
const VerificationQueue = lazy(() => import("@/pages/app/founder/VerificationQueue"));
const SecuritySettings = lazy(() => import("@/pages/app/founder/SecuritySettings"));
const AuditIntelligence = lazy(() => import("@/pages/app/founder/AuditIntelligence"));
const TrustSignals = lazy(() => import("@/pages/app/founder/TrustSignals"));
const CorridorForecasting = lazy(() => import("@/pages/app/founder/CorridorForecasting"));
const ConsolidationSignals = lazy(() => import("@/pages/app/founder/ConsolidationSignals"));
const TripPlanCanvas = lazy(() => import("@/pages/app/founder/TripPlanCanvas"));
const FlowUiRollout = lazy(() => import("@/pages/app/founder/FlowUiRollout"));
const AiReadiness = lazy(() => import("@/pages/app/founder/AiReadiness"));
const AiShadowRuns = lazy(() => import("@/pages/app/founder/AiShadowRuns"));
const GraphReasoning = lazy(() => import("@/pages/app/founder/GraphReasoning"));
const AiQuality = lazy(() => import("@/pages/app/founder/AiQuality"));
const AutonomySafety = lazy(() => import("@/pages/app/founder/AutonomySafety"));
const PricingConsole = lazy(() => import("@/pages/app/founder/PricingConsole"));
const PricingCommandCentre = lazy(() => import("@/pages/app/founder/PricingCommandCentre"));
const PricingAutomation = lazy(() => import("@/pages/app/founder/PricingAutomation"));
const PayoutsConsole = lazy(() => import("@/pages/app/founder/PayoutsConsole"));
const Reliability = lazy(() => import("@/pages/app/founder/Reliability"));
const ScaleSre = lazy(() => import("@/pages/app/founder/ScaleSre"));
const AdversarialSuite = lazy(() => import("@/pages/app/founder/AdversarialSuite"));
const Runbook = lazy(() => import("@/pages/app/founder/Runbook"));
const DeliveryConfidence = lazy(() => import("@/pages/app/founder/DeliveryConfidence"));
const CustomerSuccess = lazy(() => import("@/pages/app/founder/CustomerSuccess"));
const GrowthDashboard = lazy(() => import("@/pages/app/founder/GrowthDashboard"));
const IntelligenceProofs = lazy(() => import("@/pages/app/founder/IntelligenceProofs"));
const Simulator = lazy(() => import("@/pages/app/founder/Simulator"));
const DailyBuild = lazy(() => import("@/pages/app/founder/DailyBuild"));
const FounderLoads = lazy(() => import("@/pages/app/founder/FounderLoads"));
const FounderPayments = lazy(() => import("@/pages/app/founder/FounderPayments"));
const FounderOrganisations = lazy(() => import("@/pages/app/founder/FounderOrganisations"));

// Transporter TMS
const Earnings = lazy(() => import("@/pages/app/transporter/Earnings"));
const TrustCenter = lazy(() => import("@/pages/app/transporter/TrustCenter"));
const TmsShipments = lazy(() => import("@/pages/app/transporter/TmsShipments"));
const TmsCreate = lazy(() => import("@/pages/app/transporter/TmsCreate"));
const ReturnLoads = lazy(() => import("@/pages/app/transporter/ReturnLoads"));
const MatchPreferences = lazy(() => import("@/pages/app/transporter/MatchPreferences"));
const RecommendedOpportunities = lazy(() => import("@/pages/app/transporter/RecommendedOpportunities"));
const ReturnJourney = lazy(() => import("@/pages/app/transporter/ReturnJourney"));

// Policies
const PolicyLayout = lazy(() => import("@/pages/policies/PolicyLayout"));
const PolicyPages = () => import("@/pages/policies/PolicyPages");
const PolicyIndex = lazy(() => PolicyPages().then(m => ({ default: m.PolicyIndex })));
const ShipperTerms = lazy(() => PolicyPages().then(m => ({ default: m.ShipperTerms })));
const TransporterAgreement = lazy(() => PolicyPages().then(m => ({ default: m.TransporterAgreement })));
const LoadAcceptance = lazy(() => PolicyPages().then(m => ({ default: m.LoadAcceptance })));
const PodStandard = lazy(() => PolicyPages().then(m => ({ default: m.PodStandard })));
const CancellationPolicy = lazy(() => PolicyPages().then(m => ({ default: m.CancellationPolicy })));
const WaitingTimePolicy = lazy(() => PolicyPages().then(m => ({ default: m.WaitingTimePolicy })));
const InsuranceLiability = lazy(() => PolicyPages().then(m => ({ default: m.InsuranceLiability })));
const ProhibitedGoods = lazy(() => PolicyPages().then(m => ({ default: m.ProhibitedGoods })));

const RouteFallback = () => (
  <div className="flex min-h-[40vh] items-center justify-center text-sm text-muted-foreground">
    Loading…
  </div>
);

/** Resolves the flow_ui_enabled flag and paints the Flow UI token scope. */
const FlowUiGate = () => {
  useFlowUi();
  return null;
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <FlowUiGate />

      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AuthProvider>
          <Suspense fallback={<RouteFallback />}>
            <Routes>
              <Route element={<PublicLayout />}>
                <Route path="/" element={<Landing />} />
                <Route path="/about" element={<About />} />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/blog/guide-to-load-boards" element={<LoadBoardsGuide />} />
                <Route path="/blog/load-boards-vs-traditional-brokers" element={<LoadBoardsVsBrokers />} />
              </Route>

              <Route path="/policies" element={<PolicyLayout />}>
                <Route index element={<PolicyIndex />} />
                <Route path="shipper-terms" element={<ShipperTerms />} />
                <Route path="transporter-agreement" element={<TransporterAgreement />} />
                <Route path="load-acceptance" element={<LoadAcceptance />} />
                <Route path="pod-standard" element={<PodStandard />} />
                <Route path="cancellation" element={<CancellationPolicy />} />
                <Route path="waiting-time" element={<WaitingTimePolicy />} />
                <Route path="insurance" element={<InsuranceLiability />} />
                <Route path="prohibited-goods" element={<ProhibitedGoods />} />
              </Route>

              <Route path="/auth/login" element={<Login />} />
              <Route path="/auth/signup" element={<Signup />} />
              <Route path="/auth/onboarding" element={<Onboarding />} />
              <Route path="/onboarding" element={<OnboardingPortal />} />
              <Route path="/.lovable/oauth/consent" element={<OAuthConsent />} />

              <Route path="/app" element={<RequireAuth><AppShell /></RequireAuth>}>
                <Route index element={<RoleHomeRedirect />} />
                <Route path="shipper" element={<RequireAuth roles={["shipper"]}><ShipperDashboard /></RequireAuth>} />
                <Route path="shipper/create" element={<RequireAuth roles={["shipper"]}><CreateShipment /></RequireAuth>} />
                <Route path="shipper/shipments" element={<RequireAuth roles={["shipper"]}><ShipperShipments /></RequireAuth>} />
                <Route path="shipper/shipments/:id" element={<ShipmentDetail />} />
                <Route path="transporter" element={<RequireAuth roles={["transporter"]}><TransporterHome /></RequireAuth>} />
                <Route path="transporter/fleet" element={<RequireAuth roles={["transporter"]}><FleetPage /></RequireAuth>} />
                <Route path="transporter/availability" element={<RequireAuth roles={["transporter","ops_admin","super_admin"]}><AvailabilityBoard /></RequireAuth>} />
                <Route path="transporter/earnings" element={<RequireAuth roles={["transporter"]}><Earnings /></RequireAuth>} />
                <Route path="transporter/trust" element={<RequireAuth roles={["transporter"]}><TrustCenter /></RequireAuth>} />
                <Route path="transporter/preview" element={<RequireAuth roles={["transporter"]}><TransporterPreview /></RequireAuth>} />
                <Route path="transporter/return-loads" element={<RequireAuth roles={["transporter"]}><ReturnLoads /></RequireAuth>} />
                <Route path="transporter/preferences" element={<RequireAuth roles={["transporter"]}><MatchPreferences /></RequireAuth>} />
                <Route path="transporter/opportunities" element={<RequireAuth roles={["transporter"]}><RecommendedOpportunities /></RequireAuth>} />
                <Route path="transporter/return-journey" element={<RequireAuth roles={["transporter"]}><ReturnJourney /></RequireAuth>} />
                <Route path="transporter/tms" element={<RequireAuth roles={["transporter"]}><TmsShipments /></RequireAuth>} />
                <Route path="transporter/tms/create" element={<RequireAuth roles={["transporter"]}><TmsCreate /></RequireAuth>} />
                <Route path="driver" element={<RequireAuth roles={["driver"]}><DriverDashboard /></RequireAuth>} />
                <Route path="driver/pod/:id" element={<PodSubmit />} />
                <Route path="driver/scan" element={<RequireAuth roles={["driver","transporter"]}><ScanBarcode /></RequireAuth>} />
                <Route path="driver/mobile" element={<RequireAuth roles={["driver","transporter"]}><DriverShell /></RequireAuth>}>
                  <Route index element={<Navigate to="today" replace />} />
                  <Route path="today" element={<MobileToday />} />
                  <Route path="account" element={<MobileAccount />} />
                  <Route path="scan" element={<MobileScan />} />
                  <Route path="mismatch" element={<MobileMismatch />} />
                  <Route path="pod" element={<MobilePod />} />
                  <Route path="tracking" element={<MobileTracking />} />
                  <Route path="queue" element={<OfflineQueue />} />
                </Route>
                <Route path="admin" element={<RequireAuth roles={["ops_admin","finance_admin","compliance_admin","super_admin"]}><CommandCentre /></RequireAuth>} />
                <Route path="admin/orchestration" element={<RequireAuth roles={["ops_admin","super_admin","founder_owner"]}><FreightOrchestration /></RequireAuth>} />
                <Route path="admin/shipments" element={<GenericPage title="All shipments" />} />
                <Route path="admin/carriers" element={<GenericPage title="Carriers" />} />
                <Route path="admin/finance" element={<PaymentControlsPage />} />
                <Route path="admin/compliance" element={<GenericPage title="Compliance" />} />
                <Route path="admin/crm" element={<CrmPage />} />
                <Route path="admin/availability" element={<AvailabilityBoard />} />
                <Route path="admin/incidents" element={<IncidentsPage />} />
                <Route path="admin/templates" element={<MessageTemplatesPage />} />
                <Route path="admin/kpis" element={<KpisPage />} />
                <Route path="admin/readiness" element={<ReadinessPage />} />
                <Route path="intelligence" element={<IntelligencePage />} />
                <Route path="admin/automation" element={<RequireAuth roles={["ops_admin","super_admin","founder_owner"]}><AutomationConsole /></RequireAuth>} />
                <Route path="support" element={<SupportPage />} />
                <Route path="notifications" element={<NotificationsPage />} />
                <Route path="security" element={<SecurityCentre />} />
                <Route path="join-requests" element={<JoinRequests />} />
                <Route path="documents" element={<GenericPage title="Document center" />} />
                <Route path="settings" element={<GenericPage title="Settings" />} />
                <Route path="founder" element={<RequireAuth roles={["founder_owner"]}><FounderShell /></RequireAuth>}>
                  <Route index element={<FounderOverview />} />
                  <Route path="launch" element={<LaunchReadiness />} />
                  <Route path="security" element={<SecuritySettings />} />
                  <Route path="audit-intelligence" element={<AuditIntelligence />} />
                  <Route path="trust-signals" element={<TrustSignals />} />
                  <Route path="forecasting" element={<CorridorForecasting />} />
                  <Route path="consolidation" element={<ConsolidationSignals />} />
                  <Route path="trip-plans" element={<TripPlanCanvas />} />
                  <Route path="flow-ui" element={<FlowUiRollout />} />
                  <Route path="ai-readiness" element={<AiReadiness />} />
                  <Route path="ai-shadow" element={<AiShadowRuns />} />
                  <Route path="ai-graph" element={<GraphReasoning />} />
                  <Route path="ai-quality" element={<AiQuality />} />
                  <Route path="autonomy-safety" element={<AutonomySafety />} />
                  <Route path="switches" element={<KillSwitches />} />
                  <Route path="rules" element={<ProtectedRules />} />
                  <Route path="pricing" element={<PricingConsole />} />
                  <Route path="pricing-centre" element={<PricingCommandCentre />} />
                  <Route path="pricing-automation" element={<PricingAutomation />} />
                  <Route path="payouts" element={<PayoutsConsole />} />
                  <Route path="log" element={<ActionLog />} />
                  <Route path="verification" element={<VerificationQueue />} />
                  <Route path="flags" element={<FeatureFlagsPage />} />
                  <Route path="approvals" element={<ApprovalsQueue />} />
                  <Route path="versions" element={<RecommendationVersions />} />
                  <Route path="reliability" element={<Reliability />} />
                  <Route path="scale-sre" element={<ScaleSre />} />
                  <Route path="adversarial" element={<AdversarialSuite />} />
                  <Route path="delivery-confidence" element={<DeliveryConfidence />} />
                  <Route path="customer-success" element={<CustomerSuccess />} />
                  <Route path="growth" element={<GrowthDashboard />} />
                  <Route path="proofs" element={<IntelligenceProofs />} />
                  <Route path="simulator" element={<Simulator />} />
                  <Route path="runbook" element={<Runbook />} />
                  <Route path="daily-build" element={<DailyBuild />} />
                  <Route path="loads" element={<FounderLoads />} />
                  <Route path="organisations" element={<FounderOrganisations />} />
                  <Route path="payments" element={<FounderPayments />} />
                  <Route path="impersonate" element={<ImpersonationPanel />} />
                  <Route path="reveal" element={<RevealPanel />} />
                </Route>
              </Route>

              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
